import { FC, MouseEventHandler, useState, useRef, useEffect, useMemo } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import {
  FormControl,
  MenuItem,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ClickAwayListener,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { menuItemStyles as defaultMenuItemStyles } from '@utils/styles';

type SelectOption = {
  label: string;
  value: string;
  description?: string;
};

interface CustomSelectFieldProps {
  label: string;
  labelId: string;
  options?: SelectOption[];
  Icon?: JSX.Element;
  defaultValue?: string;
  onClick?: MouseEventHandler;
  onChange?: (value: any) => void;
  isAddNew?: boolean;
  onAddNewClick?: (value?: string) => void;
  isSearchable?: boolean;
  defaultAddNewModalOptions?: {
    onCancel: () => void;
    onConfirm: (data: SelectOption) => void;
  };
  disabled?: boolean;
  required?: boolean;
  selectStyles?: any;
  menuStyles?: SxProps<Theme>;
  menuItemStyles?: SxProps<Theme>;
  selectedValue?: string;
  onBlur?: () => void;
  validateNewItem?: (value: string) => boolean;
  error?: boolean;
  isLoading?: boolean;
}

const CustomSelectField: FC<CustomSelectFieldProps> = (props) => {
  const {
    label,
    options,
    onChange = () => {},
    isAddNew,
    disabled = false,
    required = false,
    selectStyles = {},
    menuStyles = {},
    menuItemStyles = {},
    onAddNewClick,
    defaultAddNewModalOptions,
    isSearchable = false,
    selectedValue = '',
    onBlur = () => {},
    validateNewItem = () => true,
    error = false,
    isLoading = false,
  } = props;

  const theme = useTheme();

  const [isTextFieldOpen, setIsTextFieldOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const [newItem, setNewItem] = useState('');
  const [isNewItemValid, setIsNewItemValid] = useState<boolean>();

  const displayValue = useMemo(() => {
    const matchedOptionLabel = options?.find((option) => option.value === selectedValue)?.label;
    return matchedOptionLabel ?? selectedValue;
  }, [options, selectedValue]);

  useEffect(() => {
    setSearchValue(displayValue);
  }, [displayValue]);

  const filteredOptions = useMemo(() => {
    if (isSearchable && searchValue) {
      return options?.filter((option) =>
        option?.label?.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
    return options;
  }, [options, searchValue, isSearchable]);

  const isSearchValueInOptions = useMemo(() => {
    if (searchValue)
      return filteredOptions?.some(
        (option) => option?.label?.toLowerCase() === searchValue.toLowerCase(),
      );
    return true;
  }, [filteredOptions, searchValue]);

  const handleAddNewClick = () => {
    if (onAddNewClick) onAddNewClick(isSearchValueInOptions ? '' : searchValue);
    else if (!isSearchValueInOptions) {
      setNewItem(searchValue);
      setIsTextFieldOpen(true);
    }
  };

  const handleTextFieldClose = () => {
    setIsTextFieldOpen(false);
  };

  const handleDialogClose = () => {
    defaultAddNewModalOptions?.onCancel();
    setIsDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    defaultAddNewModalOptions?.onConfirm({
      label: newItem,
      value: newItem,
    });
    handleDialogClose();
  };

  const handleTextFieldSave = () => {
    setIsNewItemValid(validateNewItem(newItem));
    setIsDialogOpen(true);
  };

  const handleSelectClose = () => {
    setIsTextFieldOpen(false);
    setNewItem('');
    setShowMenu(false);
    if (isSearchable && !searchValue.trim()) {
      onChange('');
      setSearchValue('');
    } else {
      setSearchValue(displayValue);
    }
    onBlur();
  };
  return (
    <ClickAwayListener
      onClickAway={() => {
        if (showMenu) handleSelectClose();
      }}
    >
      <FormControl fullWidth>
        <Box
          ref={selectRef}
          sx={{
            position: 'relative',
            ...selectStyles,
          }}
        >
          <TextField
            disabled={disabled}
            label={label}
            required={required}
            onClick={() => {
              if (!isSearchable) {
                setShowMenu((prev) => !prev);
              } else {
                setShowMenu(true);
              }
            }}
            value={searchValue}
            sx={{
              width: '100%',
              ...selectStyles,
              ...(!isSearchable
                ? {
                    cursor: 'pointer',
                    '& .MuiInputBase-input': {
                      cursor: 'pointer',
                    },
                  }
                : {}),
            }}
            onFocus={() => {
              if (isSearchable) {
                setShowMenu(true);
              }
              setIsTextFieldOpen(false);
            }}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (isSearchable && !e.target.value.trim()) {
                onChange('');
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isLoading && <CircularProgress size={16} sx={{ marginRight: '0.25rem' }} />}
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
              readOnly: !isSearchable,
            }}
            error={error}
          />
          {showMenu && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                zIndex: 1000,
                border: '0.5px solid #d6d6d6',
                borderRadius: '4px',
                backgroundColor: (menuItemStyles as any)?.backgroundColor || 'white',
                paddingY: '0.15rem',
                '& .Mui-selected': {
                  backgroundColor: '#51B8B026 !important',
                  '&:hover': {
                    backgroundColor: '#51B8B04D !important',
                  },
                },
                ...menuStyles,
              }}
            >
              {isTextFieldOpen ? (
                <Box
                  sx={{
                    borderRadius: '4px',
                    ...defaultMenuItemStyles,
                    ...menuItemStyles,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <TextField
                      placeholder={`New ${label}`}
                      value={newItem}
                      autoFocus
                      onChange={(e) => {
                        setNewItem(e.target.value);
                      }}
                      sx={{
                        width: '100%',
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        disabled={!newItem}
                        variant="text"
                        color="primary"
                        onClick={() => {
                          handleTextFieldSave();
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#51B8B0' }}
                      >
                        Save
                      </Button>
                      <Button
                        onClick={(e) => {
                          handleTextFieldClose();
                        }}
                        variant="text"
                        color="error"
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#FF6347' }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <>
                  {isAddNew && (
                    <MenuItem
                      onClick={() => {
                        handleAddNewClick();
                      }}
                      sx={{
                        borderRadius: '4px',
                        ...defaultMenuItemStyles,
                        color: '#51B8B0',
                        ...menuItemStyles,
                      }}
                    >
                      <AddIcon sx={{ width: '18px', height: '18px' }} />
                      Create New {isSearchValueInOptions ? '' : `'${searchValue}'`} {label}
                    </MenuItem>
                  )}
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {filteredOptions?.map(({ label: optionLabel, value, description }) => (
                      <MenuItem
                        key={value}
                        value={value}
                        selected={value === selectedValue}
                        onClick={() => {
                          onChange(value);
                          setSearchValue(optionLabel);
                          setShowMenu(false);
                        }}
                        sx={{
                          ...defaultMenuItemStyles,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: description ? 'normal' : 'nowrap',
                          display: description ? 'flex' : 'block',
                          flexDirection: description ? 'column' : undefined,
                          alignItems: description ? 'flex-start' : undefined,
                          gap: description ? '0.125rem' : undefined,
                          py: description ? '0.625rem' : undefined,
                          ...menuItemStyles,
                        }}
                      >
                        {description ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#162936',
                                lineHeight: 1.3,
                              }}
                            >
                              {optionLabel}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                color: '#6C6C6C',
                                lineHeight: 1.35,
                                whiteSpace: 'normal',
                              }}
                            >
                              {description}
                            </Typography>
                          </Box>
                        ) : (
                          optionLabel
                        )}
                      </MenuItem>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
        {isDialogOpen && (
          <Dialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: '1.75rem',
                padding: '0.5rem',
                maxWidth: '25rem',
              },
            }}
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem' }}>
              {isNewItemValid
                ? `Confirm creation of a new ${label.toLowerCase()}?`
                : `${label} not created`}
            </DialogTitle>
            <DialogContent
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: isNewItemValid ? '#6C6C6C' : '#FF6347',
              }}
            >
              {isNewItemValid
                ? `A new ${label.toLowerCase()} titled ‘${newItem}’ will be added.`
                : `${label} name cannot have special characters and numbers`}
            </DialogContent>
            <DialogActions>
              {isNewItemValid && (
                <Button
                  onClick={handleDialogConfirm}
                  variant="text"
                  color="error"
                  sx={{
                    borderRadius: '100px',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    padding: '10px 24px',
                    color: '#51B8B0',
                    textTransform: 'none',
                    '&:hover': {
                      border: 'none',
                      background: 'none',
                    },
                  }}
                >
                  Confirm
                </Button>
              )}
              <Button
                onClick={handleDialogClose}
                variant="contained"
                color="primary"
                sx={{
                  background: theme.palette.primary.main,
                  borderRadius: '100px',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  padding: '10px 24px',
                  color: '#F6F8F9',
                  boxShadow: 'none',
                  '&:hover': {
                    background: theme.palette.primary.main,
                  },
                  textTransform: 'none',
                }}
              >
                {isNewItemValid ? 'Cancel' : 'Okay'}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </FormControl>
    </ClickAwayListener>
  );
};

export default CustomSelectField;
